import { useEffect, type ReactNode } from 'react';

interface AddLineModalShellProps {
  title: string;
  submitLabel: string;
  submitting: boolean;
  canSubmit: boolean;
  error: string | null;
  onSubmit: () => void;
  onClose: () => void;
  children: ReactNode;
}

/** Shared backdrop / header / footer for the four PO-edit add modals. */
export function AddLineModalShell({
  title,
  submitLabel,
  submitting,
  canSubmit,
  error,
  onSubmit,
  onClose,
  children,
}: AddLineModalShellProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submitting, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-line-title"
      onClick={() => { if (!submitting) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-xl bg-white shadow-xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 id="add-line-title" className="text-base font-extrabold text-td-navy">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Sluiten"
            className="w-8 h-8 rounded-full flex items-center justify-center text-td-muted hover:bg-gray-100 disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          {children}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 p-5 pt-0 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-td-navy hover:bg-gray-100 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting || !canSubmit}
            className="rounded-lg bg-td-blue px-4 py-2 text-sm font-semibold text-white hover:bg-td-blue/90 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting && (
              <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" role="status" aria-hidden="true" />
            )}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
